import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { filter, isEmpty, isNil } from 'lodash';
import { GranularTemplateDropdownProps } from 'types/Dialog.types';

import TemplateSelector from './TemplateSelector';

/**
 * Renders template and mapping selectors with block headers for each available grain (MTD, QTD, YTD)
 */
const GranularTemplateDropdown = ({
  templateOptions,
  granularData,
  availableGrains,
  onGranularChange,
  numberOfBlockHeaders,
  grainBlockHeaders,
  onGrainBlockHeaderChange,
  blockHeaderOptions,
  grainTemplateLookupMap,
}: GranularTemplateDropdownProps) => {
  return (
    <>
      {availableGrains.sort().map((grain) => {
        const grainHeaders =
          grainBlockHeaders?.[grain] ||
          Object.fromEntries(Array.from({ length: numberOfBlockHeaders }, (_, i) => [`blockHeader${i + 1}`, '']));

        // Adjust for map expectations as MONTH grain exists at the map lookup level for MTD
        const lookupGrain = grain === 'MTD' ? 'MONTH' : grain;
        // Filter template options by grain for each option block
        const grainTemplateOptions = filter(templateOptions, (template) => {
          const grainTemplates = grainTemplateLookupMap?.get(lookupGrain);
          return grainTemplates ? grainTemplates.includes(template) : true;
        });

        // To track whether a template is selected for the current grain form
        const hasSelectedTemplate = !isNil(granularData[grain]?.template) && !isEmpty(granularData[grain]?.template);

        const grainTemplateBlockHeaderOptions =
          hasSelectedTemplate && blockHeaderOptions
            ? Array.from(blockHeaderOptions.get(granularData[grain].template!) || new Set<string>()).sort()
            : [];

        return (
          <Box key={grain}>
            <Typography className='grain-label' variant='h6'>
              {grain}
            </Typography>
            <Box className='template-container'>
              <TemplateSelector
                label={`${grain} Column Template`}
                value={granularData[grain]?.template || ''}
                options={grainTemplateOptions}
                onChange={(value) => onGranularChange(grain, 'template', value)}
                className='column-template-section'
              />
              <TemplateSelector
                label={`${grain} Column Mapping`}
                value={granularData[grain]?.mapping || ''}
                options={granularData[grain]?.mappingOptions || []}
                onChange={(value) => onGranularChange(grain, 'mapping', value)}
                className='column-mapping-section'
                disabled={!hasSelectedTemplate}
              />
            </Box>
            {onGrainBlockHeaderChange && (
              <Box className='block-headers-container'>
                {Array.from({ length: numberOfBlockHeaders }, (_, index) => {
                  const headerNumber = index + 1;
                  const headerKey = `blockHeader${headerNumber}` as keyof typeof grainHeaders;

                  return (
                    <TemplateSelector
                      key={headerNumber}
                      label={`Block Header ${headerNumber}`}
                      value={grainHeaders[headerKey] || ''}
                      options={grainTemplateBlockHeaderOptions || []}
                      onChange={(value) => onGrainBlockHeaderChange(grain, headerNumber, value)}
                      className='block-header-dropdown'
                      disabled={!hasSelectedTemplate}
                    />
                  );
                })}
              </Box>
            )}
          </Box>
        );
      })}
    </>
  );
};
export default GranularTemplateDropdown;
